(function () {
  const BREAKPOINT = 1024;
  const ENTRY_BREAKPOINT = 1025;
  const MAX_CACHED_FRAMES = 20;
  const PRELOAD_AHEAD = 15;
  const PRELOAD_BEHIND = 10;
  const PRELOAD_CONCURRENCY = 5;
  const RESIZE_DEBOUNCE_DELAY = 700;
  const ENTRY_FRAME_ASPECT_RATIO = 824 / 1440;
  const HANDOFF_SOURCE_FRAME_WIDTH = 1440;
  const HANDOFF_SOURCE_FRAME_HEIGHT = 824;
  const HANDOFF_CARD_WIDTH = 297;
  const HANDOFF_CARD_HEIGHT = 415;
  const HANDOFF_END_SCROLL_PROGRESS = 0.85;
  const HANDOFF_HOLD_VIEWPORT_PORTION = 0.25;
  const LOTTIE_SCRIPT_URL =
    'https://cdn.jsdelivr.net/npm/lottie-web@5.12.2/build/player/lottie.min.js';

  let lottieScriptPromise;

  function loadLottieScript() {
    if (window.lottie) return Promise.resolve(window.lottie);
    if (lottieScriptPromise) return lottieScriptPromise;

    lottieScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');

      script.src = LOTTIE_SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve(window.lottie);
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return lottieScriptPromise;
  }

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function debounce(callback, delay) {
    let timeoutId = null;

    function debounced() {
      const context = this;
      const args = arguments;

      if (timeoutId) window.clearTimeout(timeoutId);

      timeoutId = window.setTimeout(function () {
        timeoutId = null;
        callback.apply(context, args);
      }, delay);
    }

    debounced.cancel = function () {
      if (!timeoutId) return;

      window.clearTimeout(timeoutId);
      timeoutId = null;
    };

    return debounced;
  }

  function parseFrames(source) {
    if (!source) return [];

    try {
      const payload = JSON.parse(source.textContent || '[]');
      const frames = Array.isArray(payload) ? payload : payload.frames;

      if (payload && !Array.isArray(payload) && payload.entry && frames && frames.length) {
        frames[0] = payload.entry;
      }

      return Array.isArray(frames)
        ? frames.filter(function (frame) {
            return frame && (frame.desktop || frame.mobile);
          })
        : [];
    } catch (error) {
      console.warn('Card discovery sequence frames could not be parsed.', error);
      return [];
    }
  }

  function FrameStore(frames, getUrl, onReady) {
    this.frames = frames;
    this.getUrl = getUrl;
    this.onReady = onReady;
    this.cache = new Map();
    this.loading = new Map();
    this.queue = new Map();
    this.protectedIndexes = new Set();
    this.clock = 0;
    this.destroyed = false;
  }

  FrameStore.prototype.get = function (index) {
    const record = this.cache.get(index);

    if (!record) return null;

    record.lastUsed = ++this.clock;
    return record.image;
  };

  FrameStore.prototype.getNearest = function (index) {
    let nearestIndex = -1;
    let nearestDistance = Infinity;
    let image = null;

    this.cache.forEach(function (record, cachedIndex) {
      const distance = Math.abs(cachedIndex - index);

      if (distance < nearestDistance) {
        nearestIndex = cachedIndex;
        nearestDistance = distance;
        image = record.image;
      }
    });

    if (nearestIndex >= 0) {
      this.get(nearestIndex);
    }

    return image;
  };

  FrameStore.prototype.request = function (index, priority) {
    if (this.destroyed || index < 0 || index >= this.frames.length) return;
    if (this.cache.has(index) || this.loading.has(index)) return;

    const currentPriority = this.queue.get(index);

    if (currentPriority === undefined || priority > currentPriority) {
      this.queue.set(index, priority);
    }

    this.drain();
  };

  FrameStore.prototype.prime = function (index, direction) {
    const self = this;
    const safeIndex = clamp(index, 0, this.frames.length - 1);
    const directionMultiplier = direction < 0 ? -1 : 1;
    const protectedIndexes = [safeIndex];

    for (let ahead = 1; ahead <= PRELOAD_AHEAD; ahead += 1) {
      protectedIndexes.push(safeIndex + ahead * directionMultiplier);
    }

    for (let behind = 1; behind <= PRELOAD_BEHIND; behind += 1) {
      protectedIndexes.push(safeIndex - behind * directionMultiplier);
    }

    this.protectedIndexes = new Set(
      protectedIndexes.filter(function (frameIndex) {
        return frameIndex >= 0 && frameIndex < self.frames.length;
      })
    );

    this.request(safeIndex, 1000);

    for (let offset = 1; offset <= PRELOAD_AHEAD; offset += 1) {
      this.request(safeIndex + offset * directionMultiplier, 500 - offset);
    }

    for (let reverseOffset = 1; reverseOffset <= PRELOAD_BEHIND; reverseOffset += 1) {
      this.request(safeIndex - reverseOffset * directionMultiplier, 250 - reverseOffset);
    }

    this.evict();
  };

  FrameStore.prototype.drain = function () {
    const self = this;

    while (
      !this.destroyed &&
      this.loading.size < PRELOAD_CONCURRENCY &&
      this.queue.size > 0
    ) {
      const next = Array.from(this.queue.entries()).sort(function (left, right) {
        return right[1] - left[1];
      })[0];
      const index = next[0];
      const url = this.getUrl(index);

      this.queue.delete(index);

      if (!url || this.cache.has(index) || this.loading.has(index)) continue;

      let image = new Image();
      image.decoding = 'async';
      this.loading.set(index, image);

      image.onload = function () {
        const loadedIndex = Array.from(self.loading.entries()).find(function (entry) {
          return entry[1] === image;
        });

        if (!loadedIndex) return;

        const frameIndex = loadedIndex[0];
        self.loading.delete(frameIndex);

        if (!self.destroyed) {
          self.cache.set(frameIndex, {
            image: image,
            lastUsed: ++self.clock,
          });
          self.evict();
          self.onReady(frameIndex);
          self.drain();
        }
      };

      image.onerror = function () {
        const failedIndex = Array.from(self.loading.entries()).find(function (entry) {
          return entry[1] === image;
        });

        if (failedIndex) {
          self.loading.delete(failedIndex[0]);
        }

        self.drain();
      };

      image.src = url;
    }
  };

  FrameStore.prototype.evict = function () {
    const self = this;

    while (this.cache.size > MAX_CACHED_FRAMES) {
      const candidates = Array.from(this.cache.entries())
        .filter(function (entry) {
          return !self.protectedIndexes.has(entry[0]);
        })
        .sort(function (left, right) {
          return left[1].lastUsed - right[1].lastUsed;
        });

      let removable = candidates[0];

      if (!removable) {
        removable = Array.from(this.cache.entries()).sort(function (left, right) {
          return left[1].lastUsed - right[1].lastUsed;
        })[0];
      }

      if (!removable) return;

      removable[1].image.onload = null;
      removable[1].image.onerror = null;
      removable[1].image.src = '';
      this.cache.delete(removable[0]);
    }
  };

  FrameStore.prototype.destroy = function () {
    this.destroyed = true;
    this.queue.clear();

    this.cache.forEach(function (record) {
      record.image.onload = null;
      record.image.onerror = null;
      record.image.src = '';
    });
    this.loading.forEach(function (image) {
      image.onload = null;
      image.onerror = null;
      image.src = '';
    });

    this.cache.clear();
    this.loading.clear();
    this.protectedIndexes.clear();
  };

  class CardDiscoverySequence extends HTMLElement {
    connectedCallback() {
      if (this.disconnectRaf) {
        cancelAnimationFrame(this.disconnectRaf);
        this.disconnectRaf = null;
      }

      if (this.isReady) return;

      this.canvas = this.querySelector('[data-card-discovery-sequence-canvas]');
      this.framesSource = this.querySelector('[data-card-discovery-sequence-frames]');
      this.frames = parseFrames(this.framesSource);
      this.handoffLottie = null;
      this.handoffLottieContainer = null;
      this.handoffLottieRequestId = 0;

      if (!this.canvas || !this.frames.length) return;

      this.context = this.canvas.getContext('2d');
      this.currentFrameIndex = 0;
      this.previousFrameIndex = 0;
      this.sourceMode = this.isDesktop() ? 'desktop' : 'mobile';
      this.handleResize = debounce(this.handleResize.bind(this), RESIZE_DEBOUNCE_DELAY);
      this.handleDesktopChange = this.handleDesktopChange.bind(this);
      this.handleEntryChange = this.handleEntryChange.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.frameStore = null;
      this.preloadObserver = null;
      this.isPreloading = false;
      this.sectionWrapper = this.closest('[id^="shopify-section-"]');
      this.desktopMediaQuery = window.matchMedia('(min-width: ' + BREAKPOINT + 'px)');
      this.entryMediaQuery = window.matchMedia('(min-width: ' + ENTRY_BREAKPOINT + 'px)');
      this.motionMediaQuery = window.matchMedia('(prefers-reduced-motion: no-preference)');

      this.createFrameStore();
      this.frameStore.request(0, 1000);
      this.resizeCanvas();
      this.setupPreloadObserver();
      this.bindEvents();
      this.isReady = true;

      requestAnimationFrame(() => {
        this.rebuildAnimations();
        if (window.ScrollTrigger) window.ScrollTrigger.refresh();
      });
    }

    disconnectedCallback() {
      if (!this.isReady || this.disconnectRaf) return;

      this.disconnectRaf = requestAnimationFrame(() => {
        this.disconnectRaf = null;

        if (this.isConnected) return;

        this.destroyComponent();
      });
    }

    destroyComponent() {
      this.isReady = false;
      this.unbindEvents();
      this.destroyAnimations();

      if (this.preloadObserver) {
        this.preloadObserver.disconnect();
        this.preloadObserver = null;
      }

      if (this.frameStore) {
        this.frameStore.destroy();
        this.frameStore = null;
      }

      this.handleResize.cancel();
    }

    bindEvents() {
      window.addEventListener('resize', this.handleResize);
      this.desktopMediaQuery.addEventListener('change', this.handleDesktopChange);
      this.entryMediaQuery.addEventListener('change', this.handleEntryChange);
      this.motionMediaQuery.addEventListener('change', this.handleMotionChange);
    }

    unbindEvents() {
      window.removeEventListener('resize', this.handleResize);
      this.desktopMediaQuery.removeEventListener('change', this.handleDesktopChange);
      this.entryMediaQuery.removeEventListener('change', this.handleEntryChange);
      this.motionMediaQuery.removeEventListener('change', this.handleMotionChange);
    }

    isDesktop() {
      return window.matchMedia('(min-width: ' + BREAKPOINT + 'px)').matches;
    }

    getFrameUrl(index) {
      const frame = this.frames[index];

      if (!frame) return '';

      return this.sourceMode === 'desktop'
        ? frame.desktop || frame.mobile || ''
        : frame.mobile || frame.desktop || '';
    }

    createFrameStore() {
      this.frameStore = new FrameStore(
        this.frames,
        this.getFrameUrl.bind(this),
        this.onFrameReady.bind(this)
      );
    }

    onFrameReady(index) {
      if (index === this.currentFrameIndex || !this.lastDrawnImage) {
        this.renderCurrentFrame();
      }
    }

    setupPreloadObserver() {
      if (!('IntersectionObserver' in window)) {
        this.startPreloading();
        return;
      }

      this.preloadObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            this.startPreloading();
            this.preloadObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '200% 0px' }
      );
      this.preloadObserver.observe(this);
    }

    startPreloading() {
      if (this.isPreloading || !this.frameStore) return;

      this.isPreloading = true;
      this.frameStore.prime(this.currentFrameIndex, 1);
    }

    resizeCanvas() {
      const viewport = this.querySelector('.card-discovery-sequence__viewport');
      const width = viewport ? viewport.clientWidth : 0;
      const height = viewport ? viewport.clientHeight : 0;

      if (!width || !height || !this.context) return;

      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const canvasWidth = Math.round(width * pixelRatio);
      const canvasHeight = Math.round(height * pixelRatio);

      if (this.canvas.width !== canvasWidth || this.canvas.height !== canvasHeight) {
        this.canvas.width = canvasWidth;
        this.canvas.height = canvasHeight;
      }

      this.canvasRenderWidth = width;
      this.canvasRenderHeight = height;
      this.canvasPixelRatio = pixelRatio;
      this.context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      this.renderCurrentFrame();
    }

    drawCover(image) {
      if (!image || !this.context || !this.canvasRenderWidth || !this.canvasRenderHeight) return;

      const sourceWidth = image.naturalWidth || image.width;
      const sourceHeight = image.naturalHeight || image.height;

      if (!sourceWidth || !sourceHeight) return;

      const scale = Math.max(
        this.canvasRenderWidth / sourceWidth,
        this.canvasRenderHeight / sourceHeight
      );
      const drawWidth = sourceWidth * scale;
      const drawHeight = sourceHeight * scale;
      const drawX = (this.canvasRenderWidth - drawWidth) / 2;
      const drawY = (this.canvasRenderHeight - drawHeight) / 2;

      this.context.clearRect(0, 0, this.canvasRenderWidth, this.canvasRenderHeight);
      this.context.drawImage(image, drawX, drawY, drawWidth, drawHeight);
      this.lastDrawnImage = image;
    }

    renderCurrentFrame() {
      if (!this.frameStore) return;

      let image = this.frameStore.get(this.currentFrameIndex);

      if (!image) {
        image = this.frameStore.getNearest(this.currentFrameIndex);
      }

      if (image) this.drawCover(image);
    }

    setSequenceProgress(progress) {
      const frameIndex = Math.round(clamp(progress, 0, 1) * (this.frames.length - 1));
      const direction = frameIndex < this.previousFrameIndex ? -1 : 1;

      this.currentFrameIndex = frameIndex;
      this.previousFrameIndex = frameIndex;
      this.frameStore.request(frameIndex, 1000);

      if (this.isPreloading) {
        this.frameStore.prime(frameIndex, direction);
      }

      this.renderCurrentFrame();
    }

    getSequenceScrollDistance() {
      const styles = window.getComputedStyle(this);
      let pixelsPerFrame = Number.parseFloat(
        styles.getPropertyValue('--card-discovery-sequence-scroll-pixels-per-frame')
      );

      if (!Number.isFinite(pixelsPerFrame) || pixelsPerFrame <= 0) {
        pixelsPerFrame = 8;
      }

      return Math.max(window.innerHeight * 2, this.frames.length * pixelsPerFrame);
    }

    getHandoffHoldScrollDistance() {
      if (!this.getClaimTarget()) return 0;

      return Math.round(window.innerHeight * HANDOFF_HOLD_VIEWPORT_PORTION);
    }

    getScrollDistance() {
      return this.getSequenceScrollDistance() + this.getHandoffHoldScrollDistance();
    }

    getPreviousSection() {
      const previous = this.sectionWrapper && this.sectionWrapper.previousElementSibling;

      return previous && previous.querySelector('.whats-in-the-box') ? previous : null;
    }

    getNextSection() {
      const next = this.sectionWrapper && this.sectionWrapper.nextElementSibling;

      return next && next.querySelector('.claim-reward') ? next : null;
    }

    getClaimTarget() {
      const nextSection = this.getNextSection();

      if (!nextSection) return null;

      return nextSection.querySelector('[data-card-discovery-card-target]');
    }

    getHandoffStartGeometry() {
      const viewport = this.querySelector('.card-discovery-sequence__viewport');

      if (!viewport || !viewport.clientWidth || !viewport.clientHeight) return null;

      const coverScale = Math.max(
        viewport.clientWidth / HANDOFF_SOURCE_FRAME_WIDTH,
        viewport.clientHeight / HANDOFF_SOURCE_FRAME_HEIGHT
      );

      return {
        centerX: window.innerWidth / 2,
        centerY: window.innerHeight / 2,
        width: HANDOFF_CARD_WIDTH * coverScale,
        height: HANDOFF_CARD_HEIGHT * coverScale,
      };
    }

    rebuildAnimations() {
      this.destroyAnimations();
      this.resizeCanvas();

      if (!window.gsap || !window.ScrollTrigger || !this.motionMediaQuery.matches) {
        this.currentFrameIndex = 0;
        this.previousFrameIndex = 0;
        this.renderCurrentFrame();
        return;
      }

      window.gsap.registerPlugin(window.ScrollTrigger);
      this.setupSequenceTrigger();

      if (this.entryMediaQuery.matches && this.getPreviousSection()) {
        this.setupEntryTrigger();
      }

      this.setupExitTrigger();
    }

    destroyAnimations() {
      if (this.entryTween) {
        this.entryTween.kill();
        this.entryTween = null;
      }

      if (this.sequenceTrigger) {
        this.sequenceTrigger.kill();
        this.sequenceTrigger = null;
      }
      if (this.exitTween) {
        this.exitTween.kill();
        this.exitTween = null;
      }

      this.resetHandoff();

      if (window.gsap) {
        window.gsap.set(this.querySelector('.card-discovery-sequence__viewport'), {
          clearProps: 'transform,transformOrigin,opacity,width,height',
        });
      }
    }

    getEntryFrameSize() {
      const width = Math.min(window.innerWidth * 0.52, 866);

      return {
        width: width,
        height: width * ENTRY_FRAME_ASPECT_RATIO,
        x: (window.innerWidth - width) / 2,
      };
    }

    setupEntryTrigger() {
      const viewport = this.querySelector('.card-discovery-sequence__viewport');

      if (!viewport) return;

      this.entryTween = window.gsap.timeline({
        scrollTrigger: {
          trigger: this,
          start: 'top 95%',
          end: 'top top',
          scrub: 0.8,
          invalidateOnRefresh: true,
        },
        onUpdate: () => {
          this.resizeCanvas();
        },
      });

      this.entryTween.fromTo(
        viewport,
        {
          yPercent: -94,
          xPercent: -18.5,
          rotation: -36,
          width: () => this.getEntryFrameSize().width,
          height: () => this.getEntryFrameSize().height,
          x: () => this.getEntryFrameSize().x,
          transformOrigin: '50% 100%',
        },
        {
          yPercent: 0,
          xPercent: 0,
          rotation: 0,
          width: '100%',
          height: '100dvh',
          x: 0,
          ease: 'none',
        },
        0
      );

      this.resizeCanvas();
    }

    setupSequenceTrigger() {
      this.sequenceTrigger = window.ScrollTrigger.create({
        trigger: this,
        start: 'top top',
        end: () => '+=' + this.getScrollDistance(),
        pin: this,
        pinSpacing: true,
        scrub: 0.8,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: (trigger) => {
          this.updateSequenceStage(trigger.progress);
        },
        onRefresh: (trigger) => {
          this.resizeCanvas();
          this.updateSequenceStage(trigger.progress);
        },
        onLeave: () => {
          this.pinnedHandoffHoldActive = false;
        },
        onEnterBack: (trigger) => {
          this.updateSequenceStage(trigger.progress);
        },
      });
    }

    updateSequenceStage(scrollProgress) {
      const sequenceDistance = this.getSequenceScrollDistance();
      const totalDistance = this.getScrollDistance();
      const sequenceProgress = clamp(
        (scrollProgress * totalDistance) / sequenceDistance,
        0,
        1
      );
      const isHoldingHandoff =
        this.getHandoffHoldScrollDistance() > 0 && sequenceProgress >= 0.999;
      const wasHoldingHandoff = this.pinnedHandoffHoldActive;

      this.setSequenceProgress(sequenceProgress);
      this.pinnedHandoffHoldActive = isHoldingHandoff;

      if (isHoldingHandoff) {
        this.showPinnedHandoffHold();
      } else if (wasHoldingHandoff) {
        this.applyHandoffProgress(0);
      }
    }

    setupExitTrigger() {
      const target = this.getClaimTarget();
      const source = target && target.getAttribute('data-card-discovery-default-card-src');
      const image = target && target.querySelector('.js-card-image');

      if (!target || !source || !image) return;

      if (!image.complete || !image.naturalWidth) {
        image.addEventListener(
          'load',
          () => {
            if (!this.isReady) return;

            this.rebuildAnimations();
            window.ScrollTrigger.refresh();
          },
          { once: true }
        );
        return;
      }

      this.claimTarget = target;
      this.handoffWrapper = target.parentElement;
      this.handoffLottieSource = target.dataset.cardDiscoveryLottieSrc || '';
      this.handoffTargetStyle = target.getAttribute('style');
      this.handoffState = { progress: 0 };
      this.handoffGeometry = null;
      this.handoffLastProgress = null;

      this.exitTween = window.gsap.to(this.handoffState, {
        progress: 1,
        ease: 'none',
        scrollTrigger: {
          trigger: this.getNextSection(),
          start: 'top bottom',
          end: 'top top',
          scrub: true,
          invalidateOnRefresh: true,
          onRefresh: () => {
            this.measureHandoffGeometry();
            this.updateHandoffProgress(this.handoffState.progress);
          },
        },
        onUpdate: () => {
          this.updateHandoffProgress(this.handoffState.progress);
        },
      });
    }

    updateHandoffProgress(scrollProgress) {
      const safeScrollProgress = clamp(scrollProgress, 0, 1);
      const progress = clamp(safeScrollProgress / HANDOFF_END_SCROLL_PROGRESS, 0, 1);
      const crossedStart = this.handoffLastProgress <= 0 && progress > 0;
      const crossedEnd = this.handoffLastProgress >= 1 && progress < 1;
      const returnedFromClaim = progress > 0 && progress < 0.999 && !this.handoffTargetIsPrepared;

      if (crossedStart || crossedEnd || returnedFromClaim) {
        this.measureHandoffGeometry();
      }

      this.updateHandoffLottie(safeScrollProgress >= HANDOFF_END_SCROLL_PROGRESS);
      this.applyHandoffProgress(progress);
      this.handoffLastProgress = progress;
    }

    showPinnedHandoffHold() {
      if (!this.claimTarget) return;

      if (!this.handoffGeometry) {
        this.measureHandoffGeometry();
      }

      if (!this.handoffGeometry || !this.claimTarget) return;

      const start = this.handoffGeometry.start;
      const viewport = this.querySelector('.card-discovery-sequence__viewport');

      if (!this.handoffTargetIsPrepared) {
        this.prepareHandoffTarget();
      }

      window.gsap.set(this.claimTarget, {
        xPercent: -50,
        yPercent: -50,
        x: start.centerX,
        y: start.centerY,
        width: start.width,
        height: start.height,
        opacity: 1,
      });
      this.claimTarget.style.visibility = 'visible';

      if (viewport) {
        viewport.style.opacity = '0';
      }
    }

    updateHandoffLottie(shouldShow) {
      if (!shouldShow) {
        this.destroyHandoffLottie();
        return;
      }

      if (!this.handoffLottieSource || !this.claimTarget || this.handoffLottieContainer) return;

      const container = document.createElement('div');
      const requestId = this.handoffLottieRequestId + 1;

      container.className = 'card-discovery-sequence__card-lottie';
      container.setAttribute('aria-hidden', 'true');
      this.claimTarget.appendChild(container);
      this.handoffLottieContainer = container;
      this.handoffLottieRequestId = requestId;

      loadLottieScript()
        .then((lottie) => {
          if (
            !this.isReady ||
            requestId !== this.handoffLottieRequestId ||
            !this.handoffLottieContainer ||
            !this.handoffLottieContainer.isConnected
          ) {
            return;
          }

          this.handoffLottie = lottie.loadAnimation({
            container: this.handoffLottieContainer,
            renderer: 'svg',
            loop: true,
            autoplay: true,
            path: this.handoffLottieSource,
            rendererSettings: {
              progressiveLoad: true,
              preserveAspectRatio: 'xMinYMin meet',
            },
          });
        })
        .catch(() => {});
    }

    destroyHandoffLottie() {
      this.handoffLottieRequestId += 1;

      if (this.handoffLottie) {
        this.handoffLottie.destroy();
        this.handoffLottie = null;
      }

      if (this.handoffLottieContainer) {
        this.handoffLottieContainer.remove();
        this.handoffLottieContainer = null;
      }
    }

    prepareHandoffTarget() {
      if (!this.claimTarget || !this.handoffWrapper) return;

      const target = this.claimTarget;

      this.handoffWrapper.classList.add('card-discovery-sequence__handoff-wrapper');
      target.classList.add('card-discovery-sequence__handoff-target');
      target.style.visibility = 'hidden';
      target.style.opacity = '0';

      if (window.gsap) {
        window.gsap.set(target, {
          xPercent: -50,
          yPercent: -50,
        });
      }

      this.handoffTargetIsPrepared = true;
    }

    releaseHandoffTarget() {
      if (!this.claimTarget) return;

      if (window.gsap) {
        window.gsap.set(this.claimTarget, {
          clearProps: 'transform,width,height,opacity',
        });
      }

      this.claimTarget.classList.remove('card-discovery-sequence__handoff-target');
      if (this.handoffWrapper) {
        this.handoffWrapper.classList.remove('card-discovery-sequence__handoff-wrapper');
      }

      if (this.handoffTargetStyle === null) {
        this.claimTarget.removeAttribute('style');
      } else {
        this.claimTarget.setAttribute('style', this.handoffTargetStyle);
      }

      this.handoffTargetIsPrepared = false;
    }

    measureHandoffGeometry() {
      if (!this.claimTarget) return;

      this.releaseHandoffTarget();

      const wrapperRect = this.handoffWrapper.getBoundingClientRect();
      const wrapperStyles = window.getComputedStyle(this.handoffWrapper);
      const start = this.getHandoffStartGeometry();

      if (!start) return;

      this.prepareHandoffTarget();
      window.gsap.set(this.claimTarget, {
        clearProps: 'transform,width,height',
      });
      window.gsap.set(this.claimTarget, {
        xPercent: -50,
        yPercent: -50,
        x: start.centerX,
        y: start.centerY,
      });

      this.handoffGeometry = {
        start: start,
        end: {
          centerX: wrapperRect.left + wrapperRect.width / 2,
          centerY: wrapperRect.top + wrapperRect.height / 2,
          width: Number.parseFloat(wrapperStyles.width),
          height: Number.parseFloat(wrapperStyles.height),
        },
      };
    }

    updateHandoffEndPosition() {
      if (!this.handoffGeometry || !this.handoffWrapper) return;

      const wrapperRect = this.handoffWrapper.getBoundingClientRect();

      this.handoffGeometry.end.centerX = wrapperRect.left + wrapperRect.width / 2;
      this.handoffGeometry.end.centerY = wrapperRect.top + wrapperRect.height / 2;
    }

    applyHandoffProgress(progress) {
      if (!this.handoffGeometry || !this.claimTarget) return;

      this.updateHandoffEndPosition();

      const safeProgress = clamp(progress, 0, 1);
      const start = this.handoffGeometry.start;
      const end = this.handoffGeometry.end;
      const isComplete = safeProgress >= 0.999;
      const centerX = start.centerX + (end.centerX - start.centerX) * safeProgress;
      const centerY = start.centerY + (end.centerY - start.centerY) * safeProgress;
      const x = centerX;
      const y = centerY;
      const width = start.width + (end.width - start.width) * safeProgress;
      const height = start.height + (end.height - start.height) * safeProgress;
      const viewport = this.querySelector('.card-discovery-sequence__viewport');

      if (safeProgress <= 0) {
        if (this.pinnedHandoffHoldActive) {
          this.showPinnedHandoffHold();
          return;
        }

        if (!this.handoffTargetIsPrepared) {
          this.prepareHandoffTarget();
        }

        window.gsap.set(this.claimTarget, {
          xPercent: -50,
          yPercent: -50,
          x: start.centerX,
          y: start.centerY,
          width: start.width,
          height: start.height,
          opacity: 0,
        });
        this.claimTarget.style.visibility = 'hidden';

        if (viewport) {
          viewport.style.opacity = '';
        }
        return;
      }

      if (isComplete) {
        this.releaseHandoffTarget();

        if (viewport) {
          viewport.style.opacity = '0';
        }
        return;
      }

      if (!this.handoffTargetIsPrepared) {
        this.prepareHandoffTarget();
      }

      window.gsap.set(this.claimTarget, {
        xPercent: -50,
        yPercent: -50,
        x: x,
        y: y,
        width: width,
        height: height,
        opacity: 1,
      });

      this.claimTarget.style.visibility = 'visible';

      if (viewport) {
        viewport.style.opacity = '0';
      }
    }

    resetHandoff() {
      this.destroyHandoffLottie();
      this.releaseHandoffTarget();

      this.claimTarget = null;
      this.handoffWrapper = null;
      this.handoffTargetStyle = null;
      this.handoffTargetIsPrepared = false;
      this.handoffGeometry = null;
      this.handoffState = null;
      this.handoffLastProgress = null;
      this.handoffLottieSource = '';
      this.pinnedHandoffHoldActive = false;
    }

    handleResize() {
      this.resizeCanvas();

      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }

    handleDesktopChange() {
      const nextSourceMode = this.isDesktop() ? 'desktop' : 'mobile';

      if (nextSourceMode !== this.sourceMode) {
        const shouldPreload = this.isPreloading;

        this.sourceMode = nextSourceMode;
        this.frameStore.destroy();
        this.createFrameStore();
        this.frameStore.request(this.currentFrameIndex, 1000);
        this.isPreloading = false;

        if (shouldPreload) this.startPreloading();
      }

      this.rebuildAnimations();
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }

    handleEntryChange() {
      this.rebuildAnimations();
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }

    handleMotionChange() {
      this.rebuildAnimations();
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }

    scheduleRebuild() {
      if (!this.isReady) return;

      requestAnimationFrame(() => {
        this.sectionWrapper = this.closest('[id^="shopify-section-"]');
        this.rebuildAnimations();
        if (window.ScrollTrigger) window.ScrollTrigger.refresh();
      });
    }
  }

  if (!customElements.get('card-discovery-sequence')) {
    customElements.define('card-discovery-sequence', CardDiscoverySequence);
  }

  function rebuildAllSequences() {
    document.querySelectorAll('card-discovery-sequence').forEach(function (element) {
      element.scheduleRebuild();
    });
  }

  document.addEventListener('shopify:section:load', rebuildAllSequences);
  document.addEventListener('shopify:section:reorder', rebuildAllSequences);
  document.addEventListener('shopify:section:unload', rebuildAllSequences);
})();
